/**
 * useSportMarketSelection — Sport and market type selection state + hotkeys.
 *
 * Manages selectedSport, selectedMarketType, validates market type availability,
 * registers keyboard shortcuts, and notifies parent on changes.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useHotkeys } from '@/lib/hotkeys';
import { type Sport, type MarketType, getSportConfig } from '@/lib/sportsDiscovery';

interface UseSportMarketSelectionParams {
  onSportMarketChange?: (sport: Sport, marketType: MarketType) => void;
}

export interface SportMarketSelectionResult {
  selectedSport: Sport;
  selectedMarketType: MarketType;
  availableMarketTypes: MarketType[];
  useMoneylineView: boolean;
  handleSportChange: (sport: Sport) => void;
  handleMarketTypeChange: (type: MarketType) => void;
}

export function useSportMarketSelection({
  onSportMarketChange,
}: UseSportMarketSelectionParams): SportMarketSelectionResult {
  const [selectedSport, setSelectedSport] = useState<Sport>('nba');
  const [selectedMarketType, setSelectedMarketType] = useState<MarketType>('moneyline');

  // Get available market types for selected sport
  const availableMarketTypes = useMemo((): MarketType[] => {
    const config = getSportConfig(selectedSport);
    return config?.marketTypes ?? (['moneyline'] as MarketType[]);
  }, [selectedSport]);

  // Ensure selected market type is valid for selected sport
  useEffect(() => {
    if (!availableMarketTypes.includes(selectedMarketType)) {
      const firstType = availableMarketTypes[0];
      if (firstType) {
        setSelectedMarketType(firstType);
      }
    }
  }, [availableMarketTypes, selectedMarketType]);

  // Notify parent when sport/market type changes
  useEffect(() => {
    onSportMarketChange?.(selectedSport, selectedMarketType);
  }, [selectedSport, selectedMarketType, onSportMarketChange]);

  // Keyboard shortcuts for quick navigation
  useHotkeys({
    'sport-nba': () => setSelectedSport('nba'),
    'sport-nfl': () => setSelectedSport('nfl'),
    'sport-nhl': () => setSelectedSport('nhl'),
    'sport-cbb': () => setSelectedSport('cbb'),
    'sport-atp': () => setSelectedSport('tennis-atp'),
    'sport-wta': () => setSelectedSport('tennis-wta'),
    'market-moneyline': () => {
      if (availableMarketTypes.includes('moneyline')) {
        setSelectedMarketType('moneyline');
      }
    },
    'market-spread': () => {
      if (availableMarketTypes.includes('spread')) {
        setSelectedMarketType('spread');
      }
    },
    'market-total': () => {
      if (availableMarketTypes.includes('total')) {
        setSelectedMarketType('total');
      }
    },
  });

  const useMoneylineView = selectedMarketType === 'moneyline';

  const handleSportChange = useCallback((value: Sport) => {
    setSelectedSport(value);
  }, []);

  const handleMarketTypeChange = useCallback((type: MarketType) => {
    setSelectedMarketType(type);
  }, []);

  return {
    selectedSport,
    selectedMarketType,
    availableMarketTypes,
    useMoneylineView,
    handleSportChange,
    handleMarketTypeChange,
  };
}
